import { apiGet } from "./apiClient";

/**
 * Session camera video.
 *
 *   GET /sessions/{trial}/videos?device_id=  → one entry per encoded stream
 *
 * The encoder emits several streams ("camera", "global_map", "voxel_map"); we
 * only surface the camera. The others are diagnostic renders, not a video feed.
 *
 * Videos are produced by the ingestion pipeline when a session finalises — it
 * encodes the uploaded frames into an MP4. So they only exist for sessions that
 * have ENDED; a live (`in_progress`) session has nothing to play yet, which is
 * why the caller gates on status rather than on an empty list.
 *
 * An empty `videos` array is the documented "no video for this session" signal
 * (predates the feature, encode pending, or encode failed) — never a 404.
 *
 * `video_url` is a short-lived (1 hour) SAS URL, signed per request, so
 * re-fetching is what refreshes an expired link — see the retry in
 * `SessionVideoPanel`.
 */

const CAMERA_STREAM = "camera";

export interface SessionVideo {
  media_type: string;
  video_url: string;
  manifest_url: string;
  frame_count: number | null;
  duration_seconds: number | null;
  fps: number | null;
  /** Signed seconds between session start and the stream's first frame. */
  start_offset_seconds: number | null;
}

interface SessionVideosResponse {
  trial_number: string;
  device_id: string;
  videos: SessionVideo[];
}

/** The session's camera video, or `null` when it has none. */
export async function fetchSessionCameraVideo(
  trialNumber: string,
  deviceId: string,
): Promise<SessionVideo | null> {
  const data = await apiGet<SessionVideosResponse>(
    `/sessions/${encodeURIComponent(trialNumber)}/videos?device_id=${encodeURIComponent(deviceId)}`,
  );
  return (data.videos ?? []).find((v) => v.media_type === CAMERA_STREAM) ?? null;
}

/** True when the session is still running, so no encode exists yet. */
export function isLiveSession(status: string | null | undefined): boolean {
  return status === "in_progress";
}
